import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot, hydrateRoot, type HydrationOptions } from "react-dom/client";
import App from "./App";
import { APP_VERSION } from "./lib/constants";
import { fixStandaloneKeyboardGap } from "./lib/fixStandaloneKeyboardGap";
import { lockPwaZoom } from "./lib/lockPwaZoom";
import { registerServiceWorker } from "./lib/registerServiceWorker";
import "./index.css";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const isProd = import.meta.env.PROD;

function scrubPii(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
  }
  return event;
}

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: isProd ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    environment: isProd ? "production" : "development",
    release: `trace-web@${APP_VERSION}`,
    beforeSend: scrubPii,
  });
}

const rootEl = document.getElementById("root")!;
const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

// The `/` route is prerendered to static HTML at build time (see
// scripts/prerender.mjs), so #root already contains the landing markup —
// hydrate it in place. Every other route is served the empty-root shell, so
// mount a fresh client root as usual.
//
// Hydration mismatches are "recoverable" for React (it re-renders the page
// client-side) and would otherwise surface in Sentry only as a Replay
// breadcrumb without message or stack (TRACE-WEB-G). Report them as real
// exceptions, with the component stack, whenever Sentry is initialised.
const hydrationOptions: HydrationOptions | undefined = SENTRY_DSN
  ? {
      onRecoverableError: (error, info) => {
        Sentry.captureException(error, {
          extra: { componentStack: info?.componentStack },
        });
      },
    }
  : undefined;

if (rootEl.firstElementChild) {
  hydrateRoot(rootEl, app, hydrationOptions);
} else {
  createRoot(rootEl).render(app);
}

// Lock pinch/double-tap zoom in the installed PWA (iOS ignores the viewport
// meta, so this JS block is the reliable path).
lockPwaZoom();

// Recover the webview after the iOS keyboard dismisses in the installed PWA
// (otherwise the system leaves a persistent white strip at the bottom).
fixStandaloneKeyboardGap();

// Install service worker (production only — see registerServiceWorker.ts).
registerServiceWorker();
